import React, { useEffect, useRef, useState } from 'react';
import { loadGooglePlacesLibrary } from '../../services/googleMapsLoader';

const dropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  zIndex: 100000,
  margin: 0,
  padding: 4,
  listStyle: 'none',
  background: '#fff',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  boxShadow: '0 18px 40px rgba(15, 23, 42, 0.2)',
  maxHeight: 280,
  overflowY: 'auto',
};

const optionStyle = {
  width: '100%',
  border: 0,
  background: 'transparent',
  color: '#111827',
  cursor: 'pointer',
  display: 'block',
  fontSize: 14,
  lineHeight: 1.35,
  padding: '10px 12px',
  textAlign: 'left',
  borderRadius: 6,
};

const statusStyle = {
  color: '#6b7280',
  fontSize: 13,
  padding: '10px 12px',
};

const readLatLng = (location) => {
  if (!location) {
    return { latitude: 0, longitude: 0 };
  }

  const latitude = typeof location.lat === 'function' ? location.lat() : location.lat;
  const longitude = typeof location.lng === 'function' ? location.lng() : location.lng;

  return {
    latitude: Number(latitude) || 0,
    longitude: Number(longitude) || 0,
  };
};

const getDisplayName = (displayName) => {
  if (!displayName) return '';
  return typeof displayName === 'string' ? displayName : displayName.text || '';
};

const normalizePlace = (place) => {
  const { latitude, longitude } = readLatLng(place.location);
  const displayName = getDisplayName(place.displayName);
  const formattedAddress = place.formattedAddress || '';
  const addressIncludesName =
    displayName && formattedAddress.toLowerCase().includes(displayName.toLowerCase());
  const address =
    formattedAddress && displayName && !addressIncludesName
      ? `${displayName}, ${formattedAddress}`
      : formattedAddress || displayName;

  return {
    placeId: place.id || '',
    name: displayName || address,
    address,
    latitude,
    longitude,
  };
};

const getSuggestionText = (suggestion) => {
  const prediction = suggestion.placePrediction;
  const mainText = prediction?.mainText?.text || prediction?.text?.text || '';
  const secondaryText = prediction?.secondaryText?.text || '';
  const fullText = prediction?.text?.text || [mainText, secondaryText].filter(Boolean).join(', ');

  return {
    primary: mainText || fullText,
    secondary: secondaryText,
    full: fullText,
  };
};

const LocationAutocompleteInput = ({
  value,
  onChange,
  onSelect,
  onPlaceSelect,
  onSelectionClear,
  inputStyle,
  wrapperStyle,
  className,
  placeholder = 'Enter location',
  disabled = false,
  required = false,
  name,
  type = 'text',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const requestIdRef = useRef(0);
  const selectedAddressRef = useRef('');

  useEffect(() => {
    const query = String(value || '').trim();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setActiveIndex(-1);
    setErrorMessage('');

    if (disabled || query.length < 2 || query === selectedAddressRef.current) {
      setSuggestions([]);
      setIsLoading(false);
      return undefined;
    }

    const timeout = window.setTimeout(async () => {
      setIsLoading(true);

      try {
        const placesLibrary = await loadGooglePlacesLibrary();
        const AutocompleteSuggestion =
          placesLibrary.AutocompleteSuggestion || window.google?.maps?.places?.AutocompleteSuggestion;

        if (!AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
          throw new Error('Google Places autocomplete suggestions are unavailable.');
        }

        const { suggestions: googleSuggestions = [] } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            includedRegionCodes: ['in'],
            region: 'in',
          });

        if (requestIdRef.current === requestId) {
          setSuggestions(googleSuggestions.filter((suggestion) => suggestion.placePrediction));
          setIsOpen(true);
        }
      } catch (error) {
        console.warn('Google Places autocomplete failed:', error.message);
        if (requestIdRef.current === requestId) {
          setSuggestions([]);
          setErrorMessage('Location suggestions are temporarily unavailable.');
          setIsOpen(true);
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [disabled, value]);

  const updateValue = (nextValue) => {
    if (selectedAddressRef.current && nextValue !== selectedAddressRef.current) {
      selectedAddressRef.current = '';
      if (onSelectionClear) onSelectionClear();
    }

    onChange(nextValue);
    setIsOpen(true);
  };

  const selectSuggestion = async (suggestion) => {
    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ['id', 'displayName', 'formattedAddress', 'location'],
      });

      const normalizedPlace = normalizePlace(place);
      selectedAddressRef.current = normalizedPlace.address;
      requestIdRef.current += 1;
      setSuggestions([]);
      setIsOpen(false);
      setActiveIndex(-1);
      onChange(normalizedPlace.address, normalizedPlace);
      if (onSelect) onSelect(normalizedPlace);
      if (onPlaceSelect) onPlaceSelect(normalizedPlace);
    } catch (error) {
      console.warn('Google Places selection failed:', error.message);
      setErrorMessage('Unable to select this location. Please try another suggestion.');
      setIsOpen(true);
    }
  };

  const handleKeyDown = (event) => {
    if (!isOpen || !suggestions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % suggestions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showDropdown = isOpen && !disabled && (isLoading || errorMessage || suggestions.length > 0);

  return (
    <div style={{ position: 'relative', zIndex: isOpen ? 100000 : 1, ...(wrapperStyle || {}) }}>
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={(event) => updateValue(event.target.value)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => window.setTimeout(() => setIsOpen(false), 140)}
        onKeyDown={handleKeyDown}
        style={inputStyle}
        className={className}
        disabled={disabled}
        required={required}
        autoComplete="off"
      />

      {showDropdown && (
        <ul style={dropdownStyle} role="listbox">
          {isLoading && <li style={statusStyle}>Searching locations...</li>}
          {errorMessage && !isLoading && <li style={statusStyle}>{errorMessage}</li>}
          {!isLoading && !errorMessage && suggestions.map((suggestion, index) => {
            const text = getSuggestionText(suggestion);

            return (
              <li key={`${text.full}-${index}`} role="option" aria-selected={index === activeIndex}>
                <button
                  type="button"
                  style={{
                    ...optionStyle,
                    background: index === activeIndex ? '#f3f4f6' : 'transparent',
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectSuggestion(suggestion);
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{text.primary}</div>
                  {text.secondary && (
                    <div style={{ color: '#6b7280', marginTop: 2 }}>{text.secondary}</div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LocationAutocompleteInput;
